import { useCallback, useEffect, useMemo, useRef } from 'react';

import {
    getChangedLaunchTypes,
    getChangedOrphanMilestones,
    shouldAutosaveRecurrence,
} from './deadlineAutosave';
import { buildDeadlinesRollback, DeadlineWriteTarget } from './deadlineRollback';
import { deriveDaysAllowedToComplete, deriveDueDate, LAUNCH_MILESTONE_BY_TYPE } from './milestones';
import {
    findSaveRejection,
    getDeadlinesSaveFailureMessageKey,
    summariseDeadlinesSave,
} from './savingOutcome';
import { buildRemindersFromSchedule } from './useHydrateDeadlineValues';
import { buildScheduleReminders } from './useSaveDeadlines';

import type { DeadlinesRollback, DeadlineWriteAttempt } from './deadlineRollback';
import type { RemindersById } from './useHydrateDeadlineValues';
import type { DeadlineMilestoneValues } from './validation';
import type { useGetCycleEvaluation } from '@/api/cycle-evaluation-services/cycle-evaluation';
import type { EvaluationTypeValue } from '@/constants/cycle-evaluation/CycleEvaluation';
import type { FrequencyValue } from '@/constants/Frequency';
import type { CycleEvaluationGroup } from '@/types/cycle-evaluation/CycleEvaluationGroup';
import type { CycleMilestone } from '@/types/cycle-evaluation/CycleMilestone';
import type { RefObject } from 'react';

import { useUpdateCycleEvaluationGroup } from '@/api/cycle-evaluation-services/cycle-evaluation-group/cycleEvaluationGroupMutations';
import {
    useCreateCycleMilestone,
    useUpdateCycleMilestone,
} from '@/api/cycle-evaluation-services/cycle-milestone';
import { useUpdateCycleSchedule } from '@/api/cycle-evaluation-services/cycle-schedule';
import { useAmelioTranslations } from '@/hooks';
import { useDebounce } from '@/hooks/useDebounce';
import { useError } from '@/hooks/utils/useError';
import { buildCycleEvaluationGroupUpdatePayload } from '@/navigation/CycleEvaluation/cycleEvaluationGroupSteps';

const AUTOSAVE_DELAY_MS = 500;

interface DeadlineDraft {
    values: DeadlineMilestoneValues;
    remindersById: RemindersById;
    frequency?: FrequencyValue;
}

// A fixed-order tuple, so the key order of the object a caller hands over cannot make two
// identical drafts read as different ones.
const buildDeadlineDraftSignature = ({ values, remindersById, frequency }: DeadlineDraft): string =>
    JSON.stringify([values, remindersById, frequency ?? null]);

interface UseAutosaveDeadlineDatesParams {
    isReadOnly: boolean;
    hasUserEditedRef: RefObject<boolean>;
    cycleEvaluationGroupId: string | undefined;
    group: CycleEvaluationGroup | undefined;
    values: DeadlineMilestoneValues;
    orphanMilestones: CycleMilestone[];
    checkedTypes: EvaluationTypeValue[];
    cycleEvaluationQueryByType: Record<
        EvaluationTypeValue,
        ReturnType<typeof useGetCycleEvaluation>
    >;
    remindersById: RemindersById;
    frequency?: FrequencyValue;
    isSavingOnNext: boolean;
    onWritesRefused: (rollback: DeadlinesRollback) => void;
}

interface PendingDeadlineWrite {
    attempt: DeadlineWriteAttempt;
    request: Promise<unknown>;
}

const useAutosaveDeadlineDates = ({
    isReadOnly,
    hasUserEditedRef,
    cycleEvaluationGroupId,
    group,
    values,
    orphanMilestones,
    checkedTypes,
    cycleEvaluationQueryByType,
    remindersById,
    frequency,
    isSavingOnNext,
    onWritesRefused,
}: UseAutosaveDeadlineDatesParams) => {
    const lastWrittenDraftRef = useRef<string | null>(null);

    const { t } = useAmelioTranslations();
    const { handleError } = useError();

    const { mutateAsync: createCycleMilestone } = useCreateCycleMilestone();
    const { mutateAsync: updateCycleMilestone } = useUpdateCycleMilestone();
    const { mutateAsync: updateCycleSchedule } = useUpdateCycleSchedule();
    const { mutateAsync: updateCycleEvaluationGroup } = useUpdateCycleEvaluationGroup();

    // Dates, reminders and recurrence settle through ONE debounced snapshot: two would fire the
    // effect twice when an administrator picks a reminder and a date in the same breath, writing
    // the step twice.
    const edited = useMemo<DeadlineDraft>(
        () => ({ values, remindersById, frequency }),
        [values, remindersById, frequency],
    );
    const debouncedEdited = useDebounce(edited, AUTOSAVE_DELAY_MS);

    const latestRef = useRef({
        isReadOnly,
        cycleEvaluationGroupId,
        group,
        orphanMilestones,
        checkedTypes,
        cycleEvaluationQueryByType,
        createCycleMilestone,
        updateCycleMilestone,
        updateCycleSchedule,
        updateCycleEvaluationGroup,
        onWritesRefused,
        handleError,
        t,
    });
    latestRef.current = {
        isReadOnly,
        cycleEvaluationGroupId,
        group,
        orphanMilestones,
        checkedTypes,
        cycleEvaluationQueryByType,
        createCycleMilestone,
        updateCycleMilestone,
        updateCycleSchedule,
        updateCycleEvaluationGroup,
        onWritesRefused,
        handleError,
        t,
    };

    // Next sends the very same fields: recording them stops a still-pending timer from repeating it.
    const markDraftSaved = useCallback((savedDraft: DeadlineDraft) => {
        lastWrittenDraftRef.current = buildDeadlineDraftSignature(savedDraft);
    }, []);

    useEffect(() => {
        const {
            isReadOnly: readOnly,
            cycleEvaluationGroupId: groupId,
            group: currentGroup,
            orphanMilestones: currentOrphanMilestones,
            checkedTypes: currentCheckedTypes,
            cycleEvaluationQueryByType: currentQueryByType,
            createCycleMilestone: create,
            updateCycleMilestone: updateMilestone,
            updateCycleSchedule: updateSchedule,
            updateCycleEvaluationGroup: updateGroup,
            onWritesRefused: reportWritesRefused,
            handleError: reportError,
            t: translate,
        } = latestRef.current;
        const {
            values: debouncedValues,
            remindersById: debouncedRemindersById,
            frequency: debouncedFrequency,
        } = debouncedEdited;

        // Stands down while "Next" writes: a settle landing after it would re-send the step the
        // administrator has just left, moving the cycle's resume point backwards.
        if (!hasUserEditedRef.current || readOnly || !groupId || isSavingOnNext) {
            return;
        }

        const writtenDraft = buildDeadlineDraftSignature(debouncedEdited);

        if (writtenDraft === lastWrittenDraftRef.current) {
            return;
        }

        const orphanMilestonesToWrite = getChangedOrphanMilestones({
            values: debouncedValues,
            orphanMilestones: currentOrphanMilestones,
        });
        const launchTypesToWrite = getChangedLaunchTypes({
            values: debouncedValues,
            checkedTypes: currentCheckedTypes,
            remindersById: debouncedRemindersById,
            cycleEvaluationQueryByType: currentQueryByType,
        });

        const groupToWriteRecurrenceOn =
            currentGroup &&
            shouldAutosaveRecurrence({ frequency: debouncedFrequency, group: currentGroup })
                ? currentGroup
                : undefined;

        if (
            orphanMilestonesToWrite.length === 0 &&
            launchTypesToWrite.length === 0 &&
            !groupToWriteRecurrenceOn
        ) {
            return;
        }

        // The empty branch below cannot be reached from here — a milestone joins this list only
        // when its date differs from the stored one, so one with no row always carries a date. It
        // stays because `create` refuses a null `scheduledAt`, and because "Next" runs the same
        // shape over EVERY orphan, where that no-op is real.
        const orphanWrites = orphanMilestonesToWrite.flatMap<PendingDeadlineWrite>((milestone) => {
            const value = debouncedValues[milestone.id];
            const existing = currentOrphanMilestones.find(
                (row) => row.milestoneType === milestone.milestoneType,
            );
            const scheduledAt = value?.scheduledAt ? value.scheduledAt.toISOString() : null;
            const attempt: DeadlineWriteAttempt = {
                target: DeadlineWriteTarget.MILESTONE,
                id: milestone.id,
                storedValue: {
                    scheduledAt: existing?.scheduledAt ? new Date(existing.scheduledAt) : undefined,
                },
            };

            if (existing) {
                return [
                    {
                        attempt,
                        request: updateMilestone({ id: existing.id, payload: { scheduledAt } }),
                    },
                ];
            }

            return scheduledAt
                ? [
                      {
                          attempt,
                          request: create({
                              cycleEvaluationGroupId: groupId,
                              milestoneType: milestone.milestoneType,
                              scheduledAt,
                          }),
                      },
                  ]
                : [];
        });

        const scheduleWrites = launchTypesToWrite.flatMap<PendingDeadlineWrite>((type) => {
            const definition = LAUNCH_MILESTONE_BY_TYPE[type];
            const value = debouncedValues[definition.id];
            const schedule = currentQueryByType[type]?.data?.cycleSchedule;

            if (!schedule) {
                return [];
            }

            const scheduledAt = value?.scheduledAt ? value.scheduledAt.toISOString() : null;
            const daysAllowedToComplete =
                value?.scheduledAt && value.dueAt
                    ? deriveDaysAllowedToComplete({
                          scheduledAt: value.scheduledAt,
                          dueAt: value.dueAt,
                      })
                    : schedule.daysAllowedToComplete;
            const chosenReminders = debouncedRemindersById[definition.id];
            // Read BEFORE the request leaves, or an accepted sibling's refetch can replace the
            // cached schedule while this one is still in flight.
            const storedScheduledAt = schedule.scheduledAt
                ? new Date(schedule.scheduledAt)
                : undefined;
            const attempt: DeadlineWriteAttempt = {
                target: DeadlineWriteTarget.MILESTONE,
                id: definition.id,
                storedValue: {
                    scheduledAt: storedScheduledAt,
                    dueAt: storedScheduledAt
                        ? deriveDueDate({
                              scheduledAt: storedScheduledAt,
                              daysAllowedToComplete: schedule.daysAllowedToComplete,
                          })
                        : undefined,
                },
                storedReminders: buildRemindersFromSchedule(schedule.scheduleReminders),
            };

            return [
                {
                    attempt,
                    request: updateSchedule({
                        ...schedule,
                        scheduledAt,
                        daysAllowedToComplete,
                        // A type that never hydrated holds no choice to send: it keeps what the
                        // server has, so a write made for a DATE cannot carry an emptying nobody
                        // asked for.
                        scheduleReminders: chosenReminders
                            ? buildScheduleReminders({
                                  storedReminders: schedule.scheduleReminders,
                                  reminders: chosenReminders,
                              })
                            : schedule.scheduleReminders,
                    }),
                },
            ];
        });

        const recurrenceWrites: PendingDeadlineWrite[] = groupToWriteRecurrenceOn
            ? [
                  {
                      attempt: {
                          target: DeadlineWriteTarget.RECURRENCE,
                          // `null` and "never set" are the same thing to the field, which is what
                          // hydration already decided: a cycle carrying none leaves it empty.
                          storedFrequency: groupToWriteRecurrenceOn.frequency ?? undefined,
                      },
                      request: updateGroup({
                          id: groupToWriteRecurrenceOn.id,
                          payload: buildCycleEvaluationGroupUpdatePayload({
                              group: groupToWriteRecurrenceOn,
                              frequency: debouncedFrequency,
                          }),
                      }),
                  },
              ]
            : [];

        const writes = [...orphanWrites, ...scheduleWrites, ...recurrenceWrites];

        // The effect reruns on every further edit, well before a slow write returns: a stale
        // completion landing after a newer send started must not stamp the marker, or an explicit
        // resave matching that stale signature would read as already-sent and never leave.
        let isLatestSend = true;

        void Promise.allSettled(writes.map((write) => write.request)).then((results) => {
            // A settle answering for a draft nobody is looking at any more speaks too late: the
            // newer send owns the screen, and rolling back on this one's word would undo the very
            // edit that replaced it.
            if (!isLatestSend) {
                return;
            }

            const failureMessageKey = getDeadlinesSaveFailureMessageKey(
                summariseDeadlinesSave(results),
            );

            if (!failureMessageKey) {
                lastWrittenDraftRef.current = writtenDraft;

                return;
            }

            const rollback = buildDeadlinesRollback({
                attempts: writes.map((write) => write.attempt),
                results,
            });

            // Without this stamp the rollback's own render releases this effect again and re-sends
            // the writes the server ACCEPTED, against a cache that has not refreshed yet.
            lastWrittenDraftRef.current = buildDeadlineDraftSignature({
                values: { ...debouncedValues, ...rollback.values },
                remindersById: { ...debouncedRemindersById, ...rollback.remindersById },
                frequency: rollback.hasRefusedRecurrence ? rollback.frequency : debouncedFrequency,
            });

            reportWritesRefused(rollback);

            reportError({
                error: findSaveRejection(results),
                data: {
                    title: translate('errors.genericTitle'),
                    message: translate(failureMessageKey),
                },
            });
        });

        return () => {
            isLatestSend = false;
        };
    }, [debouncedEdited, isSavingOnNext]);

    return { markDraftSaved };
};

export { useAutosaveDeadlineDates, AUTOSAVE_DELAY_MS };
